import { loadRemoteModule } from '@angular-architects/native-federation';
import type { Route } from '@angular/router';

/** `federation.manifest.json`: remote name → url of its `remoteEntry.json`. */
export type FederationManifest = Record<string, string>;

export const FEDERATION_MANIFEST_URL = '/federation.manifest.json';

// Set by the shell's main.ts before the app boots. Stays null when boot could not load the file, so
// the first sparte load fetches it instead (federationManifest below) rather than leaving every
// remote "unknown" for the life of the tab. The shell bundles this lib (it is skipped from federation
// sharing), so main.ts and the app chunk share this one module instance.
let manifest: FederationManifest | null = null;

export function setFederationManifest(loaded: FederationManifest): void {
  manifest = loaded;
}

/**
 * Fetches the manifest fresh — it is a ConfigMap mount served no-cache. Rejects when it cannot be
 * loaded (unreachable, an error page, no JSON body).
 */
export async function fetchFederationManifest(): Promise<FederationManifest> {
  const response = await fetch(`${FEDERATION_MANIFEST_URL}?v=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`${FEDERATION_MANIFEST_URL} answered ${response.status}`);
  return response.json();
}

export const REMOTE_UNAVAILABLE_MESSAGE =
  'Diese Sparte ist gerade nicht erreichbar. Bitte versuche es gleich noch einmal oder lade die Seite neu.';

/**
 * The remote cannot be reached right now: its remoteEntry.json or the chunk itself failed to load
 * (pod down or restarting, connection lost — or, in a tab older than the releases the image keeps,
 * a chunk that stays gone until the page is reloaded). An outage, not a bug — callers tell the user
 * and the next attempt simply retries. `cause` carries the original rejection for the ErrorHandler
 * / Faro.
 */
export class RemoteUnavailableError extends Error {
  constructor(
    readonly remoteName: string,
    cause: unknown
  ) {
    super(`remote ${remoteName} is not reachable`, { cause });
    this.name = 'RemoteUnavailableError';
  }
}

/**
 * Loads `exposedModule` from the remote `remoteName`, registering the remote first if boot did not.
 *
 * initFederation skips a remote whose remoteEntry.json it cannot fetch (pod restarting, entry
 * missing) and loadRemoteModule by *name* can only find what boot registered — the remote would stay
 * "unknown remote" for the life of the tab. Passing the remoteEntry url instead makes the runtime
 * fetch and register the remote on first use (`ensureRemoteInitialized`), so it heals on the next
 * navigation once it is reachable; a remote boot did register is found by its base url and costs no
 * extra request.
 *
 * Deliberately no `remoteName` next to `remoteEntry`: a lazily registered remote is stored under the
 * name from its remoteEntry.json (`<app>-<sha>` in a deployed build), not under the manifest key, and
 * the runtime would look the manifest key up and throw. By url alone it resolves either way.
 *
 * A TypeError becomes a RemoteUnavailableError: that is what fetch and a dynamic import reject with
 * when the network or the server fails — remoteEntry.json or the chunk unreachable, on first use and
 * on every later load alike. A SyntaxError is ambiguous: the runtime parses remoteEntry.json without
 * checking response.ok, so an HTML 502/503/404 page (the ingress answering for a pod that is down)
 * surfaces exactly like a chunk that does not parse or a module throwing while it evaluates. The
 * server is asked which it is: only when the remote does not serve a readable remoteEntry.json right
 * now is it an outage. Everything else (an exposed module the remote does not have, a bug in the
 * module) passes through unchanged, so it reaches the ErrorHandler instead of being shown as an
 * outage forever.
 */
export async function loadSparteModule<T = unknown>(
  remoteName: string,
  exposedModule: string
): Promise<T> {
  const remoteEntry = (await federationManifest(remoteName))[remoteName];
  // Not in the manifest: a config error, nothing to register — fails with "unknown remote" as before.
  if (!remoteEntry) return loadRemoteModule<T>(remoteName, exposedModule);
  try {
    return await loadRemoteModule<T>({ remoteEntry, exposedModule });
  } catch (error: unknown) {
    const unreachable =
      error instanceof TypeError ||
      (error instanceof SyntaxError && !(await servesRemoteEntry(remoteEntry)));
    throw unreachable ? new RemoteUnavailableError(remoteName, error) : error;
  }
}

/**
 * Whether `url` serves a readable remoteEntry.json right now. Deliberately not a HEAD + `ok` check:
 * an ingress or proxy can answer 200 with an error page, which is what produced the SyntaxError in
 * the first place — so the body has to parse as JSON for the remote to count as up.
 */
async function servesRemoteEntry(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return false;
    await response.json();
    return true;
  } catch {
    return false;
  }
}

/** The `routes` a remote exposes under `exposedModule` (`./Routes`, `./Idd-Routes`). */
export function loadSparteRoutes(remoteName: string, exposedModule: string): Promise<Route[]> {
  return loadSparteModule<{ routes: Route[] }>(remoteName, exposedModule).then(m => m.routes);
}

/**
 * The manifest, fetched now when boot could not load it (ConfigMap mount not there yet, one 502):
 * the shell then booted without remotes, and this is what brings them back on the first sparte load.
 */
async function federationManifest(remoteName: string): Promise<FederationManifest> {
  if (manifest) return manifest;
  try {
    manifest = await fetchFederationManifest();
    return manifest;
  } catch (error: unknown) {
    throw new RemoteUnavailableError(remoteName, error);
  }
}
